import React from 'react';

const PeerMateFinal = () => {
  return (
    <div className="bg-[#f0f4f8] min-h-screen font-sans p-4 md:p-12 lg:p-16 flex items-center justify-center">
      
      {/* Main Floating 3D Board */}
      <main className="max-w-7xl w-full bg-white rounded-[3.5rem] shadow-[0_60px_100px_-20px_rgba(0,0,0,0.12),0_30px_60px_-30px_rgba(0,0,0,0.15)] border border-white relative overflow-hidden">
        
        {/* Go Back Button */}
        <div className="absolute top-8 left-8 md:top-12 md:left-12 z-50">
          <button 
            onClick={() => window.history.back()} 
            className="group flex items-center gap-3 bg-white border border-slate-200 py-3 px-6 rounded-2xl text-slate-900 font-black text-xs uppercase tracking-widest shadow-[0_5px_0_0_rgba(226,232,240,1)] hover:shadow-none hover:translate-y-[5px] transition-all active:scale-95"
          >
            <svg className="w-4 h-4 text-blue-600 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Go Back
          </button>
        </div>

        {/* Hero */}
        <section className="relative px-8 pt-32 pb-16 md:px-20 flex flex-col lg:flex-row items-center gap-16">
          
          <div className="flex-1 space-y-8 z-10">
            
            <div className="inline-block px-5 py-1.5 bg-blue-600 text-white rounded-full font-black text-[10px] uppercase tracking-[0.2em] shadow-[4px_4px_0px_0px_rgba(30,58,138,1)]">
              Learning Platform Guide
            </div>

            <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-[0.85] text-slate-900">
              How Peer<span className="text-blue-600">2Learn</span> Works
            </h1>

            <div className="space-y-4">
              <p className="text-xl text-slate-600 leading-relaxed font-bold max-w-lg">
                Peer2Learn is a modern learning platform that connects students with knowledgeable tutors through structured courses and flexible learning experiences.
              </p>

              <p className="text-slate-500 leading-relaxed max-w-lg font-medium">
                Tutors can create courses, share expertise, and support learners, while students can explore subjects, enroll in courses, and build academic confidence through collaborative learning.
              </p>
            </div>
          </div>

          {/* Image */}
          <div className="flex-1 relative">
            <div className="relative z-10 bg-slate-50 rounded-[3.5rem] p-4 shadow-inner border border-slate-100 transform rotate-2 hover:rotate-0 transition-all duration-700 group">

              <div className="bg-white rounded-[2.5rem] overflow-hidden shadow-2xl relative">
                <img 
                  src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=1200" 
                  alt="Students Learning" 
                  className="w-full h-[500px] object-cover grayscale brightness-110 group-hover:grayscale-0 transition-all duration-1000"
                />

                <div className="absolute inset-0 bg-blue-600/10 group-hover:bg-transparent transition-colors"></div>
              </div>

              <div className="absolute -bottom-6 -left-6 bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-2xl transform -rotate-6">
                <p className="text-blue-600 font-black text-xs uppercase tracking-[0.2em] mb-1">Get Started</p>
                <p className="font-black text-2xl tracking-tighter italic">Learn & Teach</p>
              </div>

            </div>
          </div>

        </section>

        {/* Quote */}
        <section className="px-8 md:px-20 py-12">
          <div className="bg-slate-50 rounded-[3rem] p-8 md:p-12 border border-slate-100 shadow-sm">

            <p className="text-slate-600 text-lg md:text-2xl font-medium leading-relaxed italic text-center max-w-4xl mx-auto">
              "Peer2Learn brings students and tutors together through flexible courses, collaborative learning spaces, and accessible education designed for modern learners."
            </p>

          </div>
        </section>

        {/* Steps */}
        <section className="px-8 md:px-20 pb-24">

          <div className="flex flex-col md:flex-row items-end justify-between mb-16 gap-4">
            <h2 className="text-4xl font-black text-slate-900 tracking-tightest uppercase">
              How The Platform Works
            </h2>

            <div className="h-px flex-1 bg-slate-100 mx-8 hidden md:block"></div>

            <p className="text-blue-600 font-black text-xs uppercase tracking-widest">
              Learning Steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">

            {/* 01 */}
            <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-[0_20px_40px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all group">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-[4px_4px_0_0_rgba(30,58,138,1)] mb-8">
                01
              </div>

              <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tighter">
                Create an Account
              </h3>

              <p className="text-slate-500 leading-relaxed">
                Sign up as a student or tutor to join the Peer2Learn learning community and access the platform’s courses and teaching tools.
              </p>
            </div>

            {/* 02 */}
            <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-[0_20px_40px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all group">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-[4px_4px_0_0_rgba(30,58,138,1)] mb-8">
                02
              </div>

              <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tighter">
                Tutors Create Courses
              </h3>

              <p className="text-slate-500 leading-relaxed">
                Tutors design structured courses with learning objectives, schedules, and pricing to share their expertise with students.
              </p>
            </div>

            {/* 03 */}
            <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-[0_20px_40px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all group">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-[4px_4px_0_0_rgba(30,58,138,1)] mb-8">
                03
              </div>

              <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tighter">
                Explore Courses
              </h3>

              <p className="text-slate-500 leading-relaxed">
                Students browse subjects, compare tutors, and review course descriptions to find learning opportunities that match their goals.
              </p>
            </div>

            {/* 04 */}
            <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-[0_20px_40px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all group">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-[4px_4px_0_0_rgba(30,58,138,1)] mb-8">
                04
              </div>

              <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tighter">
                Enroll in Courses
              </h3>

              <p className="text-slate-500 leading-relaxed">
                Students enroll in courses and gain access to lessons, tutor interaction, and structured learning sessions.
              </p>
            </div>

            {/* 05 */}
            <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-[0_20px_40px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all group">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-[4px_4px_0_0_rgba(30,58,138,1)] mb-8">
                05
              </div>

              <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tighter">
                Learn & Collaborate
              </h3>

              <p className="text-slate-500 leading-relaxed">
                Participate in online lessons, one-on-one tutoring, or collaborative learning sessions to strengthen understanding.
              </p>
            </div>

            {/* 06 */}
            <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-[0_20px_40px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all group">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-[4px_4px_0_0_rgba(30,58,138,1)] mb-8">
                06
              </div>

              <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tighter">
                Track Your Progress
              </h3>

              <p className="text-slate-500 leading-relaxed">
                Use your dashboard to monitor course progress, review lessons, and stay organized throughout your learning journey.
              </p>
            </div>

          </div>
        </section>

        {/* Community Section */}
        <section className="bg-slate-900 text-white px-8 md:px-20 py-24 flex flex-col items-center relative overflow-hidden">

          <div className="absolute top-0 left-0 w-full h-1 bg-blue-600 shadow-[0_0_20px_2px_rgba(37,99,235,1)]"></div>

          <div className="max-w-4xl text-center z-10 space-y-8">

            <h2 className="text-5xl md:text-7xl font-black tracking-tightest uppercase leading-[0.9]">
              Learn. Teach. <br/> <span className="text-blue-600">Grow Together.</span>
            </h2>

            <p className="text-slate-400 text-lg md:text-xl leading-relaxed">
              Peer2Learn creates a collaborative learning ecosystem where students gain knowledge and tutors share expertise through flexible courses and interactive education.
            </p>

          </div>
        </section>

        {/* Footer */}
        <section className="p-12 md:p-20 text-center">

          <p className="max-w-3xl mx-auto text-slate-500 font-medium mb-10 text-lg">
            Peer2Learn empowers students and tutors to connect, collaborate, and achieve academic success through modern learning experiences.
          </p>

          <div className="w-24 h-1 bg-blue-600 mx-auto mb-12"></div>

          <p className="text-[10px] text-slate-400 font-black tracking-[0.5em] uppercase">
            © 2026 PEER2LEARN INTERNATIONAL – ADVANCING ACADEMIC EXCELLENCE
          </p>

        </section>

      </main>
    </div>
  );
};

export default PeerMateFinal;